import { CaretDownOutlined, CaretUpOutlined, CheckCircleOutlined, DownloadOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Observable, useLazyQuery } from '@apollo/client';
import { List, Table, Tooltip, Button } from 'antd';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { GET_REPORT_QUERY } from '../../gql/queries/GET_REPORT_QUERY';
import { apolloService } from '../../services';
import { authService } from '../../auth/authService';
import { download } from '../../updated/utils';
import classNames from 'classnames';
import { useCreateReportMutation } from '../../gql/hooks';

const DownloadButton = ( { id } ) => {

    const [ checkReport, { loading, error, data } ] = useLazyQuery( GET_REPORT_QUERY, { fetchPolicy: 'network-only' } );

    useEffect( () => {
        if ( data ) {
            download( data.report.node.reportUrl, data.report.node.reportType );
        } 
    }, [ data, error ] );

    return <Button
        onClick={ () => {
            checkReport( { variables: { reportId: id } } );
        } }>
        {
            loading === true ?
                <LoadingOutlined /> :
                <DownloadOutlined />
        }
    </Button>;
};

const columns = [
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: ( status, record ) => {
            const [ text, icon ] = 
                status === 'COMPLETED' ? [ `Job completed at ${ new Date( record.reportCompletedAt ).toLocaleString() }`, <CheckCircleOutlined className='success' key={ record.id } /> ]
                    : status === 'IN_PROCESS' ? [ null, <LoadingOutlined spin className='pending' key={ record.id } /> ]
                        //: status === 'aborted' ? [ `Job aborted at ${ new Date( record.reportCompletedAt ).toLocaleString() }`, <StopOutlined className='aborted' /> ]
                        : [ `Error: ${ record.error }`, <ExclamationCircleOutlined className='error' key={ record.id } /> ];

            return text ?
                <Tooltip title={ text }>
                    { icon }
                </Tooltip>
                : icon ;
            
        },
        className: 'status'
    },
    {
        title: 'Started',
        dataIndex: 'reportStartedAt',
        key: 'startTime',
        render: start => new Date( start ).toLocaleString()
    },
    {
        title: 'Job Duration',
        dataIndex: 'reportCompletedAt',
        key: 'finishTime',
        render: ( finish, record ) => {
            if ( !finish ) {return '';}
            const [ start, end ] = [ moment( new Date( record.reportStartedAt ) ), moment( new Date( finish ) ) ];
            const duration = moment.duration( end.diff( start ) );

            const [ hours, minutes, seconds, msecs ] = [ duration.hours(), duration.minutes(), duration.seconds(), duration.milliseconds() ];

            if ( hours === 0 && minutes === 0 && seconds === 0 ) {
                return `${ msecs } ms`;
            }
            const strArr = [ `${ seconds } s` ];
            if ( hours > 0 || minutes > 0 ) {
                strArr.unshift( `${ minutes } min` );
            }
            if ( hours > 0 ) {
                strArr.unshift( `${ hours } hr` );
            }

            return strArr.join( ' ' );
        }
    },
    {
        title: 'Download',
        dataIndex: 'id',
        key: 'id',
        render: ( id, item ) => {
            if ( item.status !== 'COMPLETED' ) {return;}

            return <DownloadButton id={ id } />;
        },
        className: 'download'
    }
];

async function fetchReport( report ) {
    try {
        const response = await apolloService.query( {
            query: GET_REPORT_QUERY,
            variables: {
                reportId: report.id
            },
            context: { headers: { authorization: `Bearer ${ authService.getAccessToken() }` } },
            fetchPolicy: 'network-only'
        } );

        return response.data?.report?.node;
    } catch ( e ) {
        return {
            ...report,
            status: 'ERROR',
            error: 'Could not get status of report'
        };
    }

}

function watchReport( report ) {
    return new Observable( async sub => {
        let reportInProgress = true;
        let result = null;
        while ( reportInProgress ) {
            await new Promise( res => setTimeout( res, 1000 ) );

            result = await fetchReport( report );

            reportInProgress = result.status === 'IN_PROCESS';
        }

        sub.next( result );
        sub.complete();
    } );
}

export const DataExportItem = ( { id, name, type, description, history, historyLoading } ) => {

    const [ createReport, { data: reportCreation } ] = useCreateReportMutation();
    const [ showHistory, setShowHistory ] = useState( false );
    const [ enableRun, setEnableRun ] = useState( true );

    const [ subscriptions, setSubscriptions ] = useState( [] );

    const [ currentJob, setCurrentJob ] = useState( null );
    const [ localData, setLocalData ] = useState( [] );     // reports run durring this session
    const [ historyData, setHistoryData ] = useState( [] ); // historic reports

    const historicDataSorted = useMemo( () => {
        return historyData.reverse();
    }, [ historyData ] );

    // aggregate table data
    const tableData = useMemo( () => { 
        return [
            ...( currentJob ? [ currentJob ] : [] ),
            ...localData,
            ...historicDataSorted
        ];
    }, [ localData, currentJob, historicDataSorted ] ); 
    
    const mostRecentJob = useMemo( () => tableData?.[ 0 ], [ tableData ] );

    // executes once history is loaded and watches pending jobs
    useEffect( () => {
        if ( !history || historyLoading ) {return;}
        let scopedHistory = history.map( h => h );

        setHistoryData( scopedHistory );

        history.filter( report => report.status === 'IN_PROCESS' )
            .forEach( report => {
                const subWatch = watchReport( report ).subscribe( {
                    next( result ) { 
                        // remap history to include new finished item
                        scopedHistory = scopedHistory.map( rep => rep.id === result.id ? result : rep );
                        setHistoryData( scopedHistory );
                    },
                    error( error ) { 
                        //todo
                    }
                } );
                setSubscriptions( [ subWatch, ...subscriptions ] );

            } );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ historyLoading ] );

    // clean up observables on destroy
    useEffect( () => {
        return () => {
            subscriptions.forEach( s => s?.unsubscribe() );
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    //
    const requestReport = ( ) => {
        const time = new Date().toISOString();
        const job = {
            reportStartedAt: time,
            id: `temp_id_${ time }`,
            status: 'IN_PROCESS'
        };
        setCurrentJob( job );
        setEnableRun( false );

        const sub = createReport( {
            variables: {
                reportType: type
            }
        } )
            .catch( e => {
                const report = {
                    ...job,
                    status: 'ERROR',
                    error: 'Could not run report'
                };
    
                setLocalData( [ report, ...localData ] );
                setCurrentJob( null );
                setEnableRun( true );
            } );
        setSubscriptions( [ sub, ...subscriptions ] );
    };

    // executes once job has been created and adds it to list
    useEffect( () => {
        
        if ( !reportCreation ) {return;}

        const report = {
            ...currentJob,
            ...reportCreation.createReport
        };
        const updatedLocalData = [ report, ...localData ];

        setLocalData( updatedLocalData );

        setCurrentJob( null );

        setTimeout( () => setEnableRun( true ), 1000 );

        const subWatch = watchReport( report ).subscribe( {
            next( result ) { 
                // remap history to include new finished item
                let hist = updatedLocalData.map( rep => rep.id === result.id ? result : rep );
                setLocalData( hist );
            }
        } );
        setSubscriptions( [ subWatch, ...subscriptions ] );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ reportCreation ] );

    return (
        <List.Item key={ id } className='data-export-item'>
            <div className='main-section'>
                <List.Item.Meta
                    title={ name }
                    description={ <div>
                        { description }
                        <br/><br />
                        <a 
                            onClick={ () => setShowHistory( !showHistory ) }>
                            { showHistory ? 'Hide History' : 'Show History' }
                            { showHistory ? <CaretUpOutlined/> : <CaretDownOutlined /> }
                        </a>
                    </div> }

                />
                <div className='run-report'>
                    <Button
                        type="link"
                        disabled={ !enableRun }
                        onClick={ () => requestReport( id ) }>
                            Run Report
                    </Button>
                    <span>{ mostRecentJob ? `Last run on ${ new Date( mostRecentJob.reportStartedAt ).toLocaleString() }` : '' }</span>
                </div>
            </div>
                

            <div className={ classNames( 'history', { open: showHistory } ) }  >
                <Table
                    dataSource={ tableData }
                    loading={ historyLoading }
                    columns={ columns }
                    rowKey='id'
                    rowClassName={ record => record.status == 'IN_PROCESS' ? 'pending-record' : '' }
                >

                </Table>
            </div>
            
        </List.Item> );
};